import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

type TierRow = {
  tier: string;
  package_id: string | null;
  package_name: string | null;
  listings: number;
  impressions: number;
  clicks: number;
  ctr: number;
};

type ListingRow = {
  listing_id: string;
  listing_type: string;
  tier: string;
  package_id: string | null;
  impressions: number;
  clicks: number;
  ctr: number;
};

const startOfMonth = () => {
  const d = new Date();
  d.setDate(1); d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

export function FeaturedListingAnalytics() {
  const [from, setFrom] = useState(startOfMonth());
  const [to, setTo] = useState(today());
  const [tierRows, setTierRows] = useState<TierRow[]>([]);
  const [listingRows, setListingRows] = useState<ListingRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const _from = new Date(from + 'T00:00:00').toISOString();
    const _to = new Date(to + 'T23:59:59').toISOString();
    const [a, b] = await Promise.all([
      (supabase as any).rpc('featured_listing_tier_report', { _from, _to }),
      (supabase as any).rpc('featured_listing_listing_report', { _from, _to, _tier: null }),
    ]);
    setTierRows(a.data || []);
    setListingRows(b.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold">Featured Listing Analytics</h1>
        <p className="text-sm text-muted-foreground">
          Impressions and clicks recorded against listings while they have an active package boost.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle>Date range</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-4 items-end">
          <div><Label>From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><Label>To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          <button onClick={load} className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm">Refresh</button>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>By package tier</CardTitle>
          <CardDescription>Aggregated across every listing in each package &amp; tier.</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Tier</TableHead><TableHead>Package</TableHead>
              <TableHead className="text-right">Listings</TableHead>
              <TableHead className="text-right">Impressions</TableHead>
              <TableHead className="text-right">Clicks</TableHead>
              <TableHead className="text-right">CTR %</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {tierRows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="capitalize">{r.tier}</TableCell>
                  <TableCell>{r.package_name || <span className="text-muted-foreground">— manual / no package</span>}</TableCell>
                  <TableCell className="text-right">{r.listings}</TableCell>
                  <TableCell className="text-right">{r.impressions}</TableCell>
                  <TableCell className="text-right">{r.clicks}</TableCell>
                  <TableCell className="text-right">{Number(r.ctr).toFixed(2)}</TableCell>
                </TableRow>
              ))}
              {!tierRows.length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No events in range</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Top boosted listings</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Listing</TableHead><TableHead>Type</TableHead><TableHead>Tier</TableHead>
              <TableHead className="text-right">Impressions</TableHead>
              <TableHead className="text-right">Clicks</TableHead>
              <TableHead className="text-right">CTR %</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {listingRows.slice(0, 100).map((r) => (
                <TableRow key={r.listing_id + r.tier}>
                  <TableCell className="font-mono text-xs">{r.listing_id.slice(0, 8)}…</TableCell>
                  <TableCell className="capitalize">{r.listing_type}</TableCell>
                  <TableCell className="capitalize">{r.tier}</TableCell>
                  <TableCell className="text-right">{r.impressions}</TableCell>
                  <TableCell className="text-right">{r.clicks}</TableCell>
                  <TableCell className="text-right">{Number(r.ctr).toFixed(2)}</TableCell>
                </TableRow>
              ))}
              {!listingRows.length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No events in range</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default FeaturedListingAnalytics;
